import { getStorageItem, removeStorageItem, setStorageItem } from 'utils/ui/storage';
import { EErrorCode, isEErrorCode } from 'config/errors-codes';
import { validateDealerSelection } from './dealerResolution';
import type { IDealerIdValidationContext } from './dealerIdValidator';
import type { IUnavailableDealerId, TDealerSelectionValidation } from './dealerResolution.types';
import {
  DEALER_ID_STORAGE_KEY,
  LAST_CONFIRMED_DEALER_ID_STORAGE_KEY,
  UNAVAILABLE_DEALER_IDS_STORAGE_KEY,
} from './dealerStorageKeys';

/**
 * Validates a dealer selection and, only when it is valid, stores it under `DEALER_ID_STORAGE_KEY`.
 * Never touches `userSession()`: the selection is fixed for the login and must not reset the session.
 * A rejected selection writes nothing, so a previously committed ID stays in place.
 */
export function commitDealerSelection(
  dealerId: string,
  context: IDealerIdValidationContext
): TDealerSelectionValidation {
  const validation = validateDealerSelection(dealerId, context);

  if (validation.ok) {
    setStorageItem('sessionStorage', DEALER_ID_STORAGE_KEY, dealerId);
  }

  return validation;
}

/**
 * Drops the stored selection (fresh sign-in), so the next login runs the selection step from scratch.
 * Also drops the Dealer IDs ruled out this visit; the last confirmed Dealer ID (localStorage) survives,
 * since it is a cross-entrance pre-selection hint.
 */
export function clearDealerSelection(): void {
  removeStorageItem('sessionStorage', DEALER_ID_STORAGE_KEY);
  removeStorageItem('sessionStorage', UNAVAILABLE_DEALER_IDS_STORAGE_KEY);
}

/**
 * Remembers which Dealer IDs a step 2/3 failure ruled out for the rest of the visit, one entry per
 * ID, latest last; entries for the same ID are replaced in place. Also drops the stored selection, so
 * a reload shows the window instead of retrying the same failing ID.
 */
export function markDealerIdsUnavailable(dealerIds: readonly string[], code: EErrorCode): void {
  const remaining = readUnavailableDealerIds().filter((entry) => !dealerIds.includes(entry.dealerId));
  const next: IUnavailableDealerId[] = [...remaining, ...dealerIds.map((dealerId) => ({ dealerId, code }))];

  setStorageItem('sessionStorage', UNAVAILABLE_DEALER_IDS_STORAGE_KEY, next);
  removeStorageItem('sessionStorage', DEALER_ID_STORAGE_KEY);
}

/** The Dealer IDs ruled out this visit; malformed stored data gives an empty list. There is no `any`. */
export function readUnavailableDealerIds(): IUnavailableDealerId[] {
  const stored: unknown = getStorageItem('sessionStorage', UNAVAILABLE_DEALER_IDS_STORAGE_KEY);

  if (!Array.isArray(stored)) {
    return [];
  }

  return stored.filter(isUnavailableDealerId);
}

function isUnavailableDealerId(entry: unknown): entry is IUnavailableDealerId {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    typeof (entry as { dealerId?: unknown }).dealerId === 'string' &&
    isEErrorCode((entry as { code?: unknown }).code)
  );
}

/** Remembers the last successfully committed Dealer ID across entrances, as a pre-selection hint only. */
export function rememberConfirmedDealerId(dealerId: string): void {
  setStorageItem('localStorage', LAST_CONFIRMED_DEALER_ID_STORAGE_KEY, dealerId);
}

/** The last confirmed Dealer ID, or `undefined` when nothing, or something other than a string, is stored. */
export function readLastConfirmedDealerId(): string | undefined {
  const stored: unknown = getStorageItem('localStorage', LAST_CONFIRMED_DEALER_ID_STORAGE_KEY);

  return typeof stored === 'string' ? stored : undefined;
}
